"use client";

import { Combobox } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ExclamationCircleIcon, StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarFillIcon } from "@heroicons/react/24/solid";
import { useDebounce } from "hooks/use-debounce";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { startTransition, useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { links } from "@/data/links";
import { useActiveWatchlist } from "@/features/watchlist/services/get-active-watchlist";
import { useUpdateActiveWatchlist } from "@/features/watchlist/services/update-active-watchlist";
import { useLang } from "@/hooks/use-lang";
import { formatSymbol } from "@/utils/i18n/format-symbol";

import { SymbolData, useSearchSymbol } from "../services/search-symbol";

type Props = {
  defaultSymbols: SymbolData[];
};

export const SearchCard = ({ defaultSymbols }: Props) => {
  const router = useRouter();
  const { lang, t } = useLang();

  const [query, setQuery] = useState("");
  const deferredQuery = useDebounce(query, 300);

  const { data, isError, isLoading } = useSearchSymbol(deferredQuery);

  const displaySymbols: SymbolData[] =
    !!deferredQuery && !isLoading && !isError ? data : defaultSymbols;

  const { mutate: updateWatchlist } = useUpdateActiveWatchlist();
  const { data: activeWatchlist } = useActiveWatchlist();
  const symbols = activeWatchlist?.symbols ?? [];

  const toggleSymbolInWatchlist = ({ symbol }: { symbol: string }) => {
    const symbolExists = symbols.includes(symbol);
    const newSymbols = symbolExists
      ? symbols.filter((s) => s !== symbol)
      : symbols.concat(symbol);

    updateWatchlist(
      { symbols: newSymbols },
      {
        onSuccess: () => {
          toast.success(
            symbolExists
              ? t.layout.watchlist.removeSymbolSuccessToast(symbol)
              : t.layout.watchlist.addSymbolSuccessToast(symbol)
          );
        },
        onError: () => {
          toast.error(t.error.serverError);
        },
      }
    );
  };

  return (
    <div className="mx-4 max-w-xl -translate-y-12 transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all @xl:mx-auto">
      <Combobox value={query}>
        <div className="relative">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          <Combobox.Input
            className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0"
            placeholder={t.analysis.search.placeHolder}
            onChange={(event) => {
              startTransition(() => {
                setQuery(event.target.value);
              });
            }}
            aria-label="search symbols"
          />
        </div>

        {displaySymbols.length > 0 && (
          <Combobox.Options
            static
            className="max-h-96 w-full scroll-py-3 overflow-y-auto p-3"
          >
            {displaySymbols.map((item) => (
              <Combobox.Option key={item.symbol} value={item.symbol}>
                {({ active }) => (
                  <Link
                    href={`/${lang}${links.myAnalysisOverview({
                      symbol: item.symbol,
                    })}`}
                    className={twMerge(
                      "flex select-none items-center rounded-xl p-3",
                      active && "bg-sky-700/5"
                    )}
                    aria-label={`navigate to overview of ${item.symbol}`}
                  >
                    {/*
                     * Note:
                     * Adding the `min-width: 0` can enable the nested child to truncate properly
                     * See also: https://www.youtube.com/watch?v=2-odCIy0KOQ
                     */}
                    <div className="mx-4 min-w-0">
                      <p
                        className={twMerge(
                          "text-sm font-medium",
                          active ? "text-gray-900" : "text-gray-700"
                        )}
                      >
                        {formatSymbol(item.symbol)}
                      </p>
                      <p
                        className={twMerge(
                          "truncate text-sm @lg:hidden",
                          active ? "text-gray-700" : "text-gray-500"
                        )}
                      >
                        {item.name}
                      </p>
                    </div>
                    <p
                      className={twMerge(
                        "hidden max-w-max truncate text-sm @lg:block",
                        active ? "text-gray-700" : "text-gray-500"
                      )}
                    >
                      {item.name}
                    </p>
                    <div className="ml-auto flex shrink-0 items-center gap-5">
                      <Image
                        className="h-5 w-5 rounded-full object-cover object-[20%]"
                        alt={`${item.market} Flag Icon`}
                        width={20}
                        height={20}
                        src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${item.market}.svg`}
                      />
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (!activeWatchlist) router.push(links.authSignup);
                          else toggleSymbolInWatchlist({ symbol: item.symbol });
                        }}
                        className="relative focus:outline-none"
                        aria-label={
                          symbols.includes(item.symbol)
                            ? `remove ${item.symbol} from watchlist`
                            : `add ${item.symbol} to watchlist`
                        }
                      >
                        <StarIcon className="relative h-[30px] w-[30px] cursor-pointer text-zinc-400" />
                        {symbols.includes(item.symbol) && (
                          <StarFillIcon className="absolute left-0 top-0 h-[30px] w-[30px] animate-fadeIn cursor-pointer text-yellow-400" />
                        )}
                      </button>
                    </div>
                  </Link>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}

        {query !== "" && displaySymbols.length === 0 && (
          <div className="px-6 py-14 text-center text-sm @lg:px-14">
            <ExclamationCircleIcon
              type="outline"
              name="exclamation-circle"
              className="mx-auto h-6 w-6 text-gray-400"
            />
            <p className="mt-4 font-semibold text-gray-900">
              {t.analysis.search.noResultsFound}
            </p>
            <p className="mt-2 text-gray-500">
              {t.analysis.search.noSymbolsFound}
            </p>
          </div>
        )}
      </Combobox>
    </div>
  );
};
